import formatHrefURL from 'ha/utils/formatHrefURL';
import encodeSearchQuery from 'ha/utils/encodeSearchQuery';
import buildParams from 'ha/utils/buildParams';
import isAbsoluteUrl from 'ha/utils/isAbsoluteUrl';
import searchUrl from 'ha/utils/buildSearchQuery';
import { Language } from 'ha/i18n/types';
import { Kinds } from 'ha/pages/Inbox/constants';
import { HowItWorksUrls } from 'ha/constants/HowItWorksUrls';

import { COUNTRIES_TRANSLATED } from 'ha/constants/countriesTranslated';
import { socialUrls } from './constants';

const landlordsLangToPath = {
  [Language.nl]: 'Nederland/verhuurders',
  [Language.de]: 'Deutschland/vermieter',
};

const communityLangToPath = {
  [Language.en]: 'community',
};

const helpCenterLangToPath = {
  [Language.en]: 'help-center',
  [Language.it]: 'centro-di-assistenza',
  [Language.es]: 'centro-de-ayuda',
  [Language.de]: 'hilfe-center',
  [Language.fr]: 'centre-d-assistance',
  [Language.nl]: 'helpcenter',
};

const rentRadarLanguanges = [
  Language.de,
  Language.nl,
  Language.es,
  Language.it,
];

const rentRadarLanguangesWithLeadFormSupported = [Language.de, Language.nl];

const krugerLangToPath = {
  [Language.en]:
    '/housinganywhere-acquires-german-classifieds-website-studenten-wg',
  [Language.de]:
    '/de/housinganywhere-uebernimmt-die-deutsche-website-fuer-kleinanzeigen-studenten-wg',
  [Language.es]:
    '/es/housinganywhere-adquiere-studenten-wg-el-sitio-web-aleman-de-anuncios-clasificados',
  [Language.it]:
    '/it/housinganywhere-acquisisce-studenten-wg-il-sito-di-annunci-immobiliari-tedesco',
  [Language.nl]:
    '/nl/housinganywhere-neemt-duitse-marktplaats-studenten-wg-over',
};

const answersLangToPath = {
  [Language.en]: 'https://answers.housinganywhere.com/en/',
  [Language.es]: 'https://answers.housinganywhere.com/es/',
  [Language.fr]: 'https://answers.housinganywhere.com/fr/',
  [Language.de]: 'https://answers.housinganywhere.com/de/',
  [Language.it]: 'https://answers.housinganywhere.com/it/',
  [Language.pt]: 'https://answers.housinganywhere.com/pt/',
  [Language.nl]: 'https://answers.housinganywhere.com/nl/',
};

const capitalizeFirst = (text: string) =>
  text.substr(0, 1).toUpperCase() + text.substr(1);

interface Config {
  baseDomain: string;
  lang: Language;
}

export default class UrlResolver {
  private baseDomain: string;

  private lang: Language;

  static create = (config: Config) => new UrlResolver(config);

  constructor({ baseDomain, lang }: Config) {
    this.baseDomain = baseDomain;
    this.lang = lang;
  }

  applyFilterToSearchUrl = (pathname: string, slug?: string) => {
    return `${pathname}${slug ? `/${slug}` : ''}`;
  };

  getBaseUrl() {
    return this.baseDomain;
  }

  absolutizePath(
    path: string,
    {
      skipAddLanguage = false,
      lang,
    }: { skipAddLanguage?: boolean; lang?: Language } = {},
  ) {
    if (isAbsoluteUrl(path)) {
      return path;
    }

    return skipAddLanguage
      ? `${this.getBaseUrl()}${path}`
      : `${this.getBaseUrl()}${this.formatHrefURL(path, lang)}`;
  }

  formatHrefURL(path: string, lang?: Language) {
    return formatHrefURL(path, lang || this.lang);
  }

  getAnswersUrl = () => answersLangToPath[this.lang];

  getBlogUrl = () => 'http://blog.housinganywhere.com';

  getBrowserNotSupportedUrl = () =>
    this.formatHrefURL('/browser-not-supported');

  getCareersUrl = () => 'https://careers.housinganywhere.com/';

  getDevelopersUrl = () => 'https://developers.housinganywhere.com/';

  getCommunityUrl = (lang: Language) =>
    this.formatHrefURL(`/${communityLangToPath[lang]}`, lang);

  hasCommunityUrl = (lang: Language) => Boolean(communityLangToPath[lang]);

  getLandlordsCommunityUrl = (lang: Language) =>
    this.formatHrefURL(`/${landlordsLangToPath[lang]}`, lang);

  hasLandlordsCommunityUrl = (lang: Language) =>
    Boolean(landlordsLangToPath[lang]);

  getHelpCenterUrl = (lang: Language) =>
    this.formatHrefURL(`/${helpCenterLangToPath[lang]}`, lang);

  hasHelpCenterUrl = (lang: Language) => Boolean(helpCenterLangToPath[lang]);

  getDashboardUrl() {
    return this.formatHrefURL('/my');
  }

  getShareRoomUrl = (
    roomId: number,
    { verified }: { verified?: boolean } = {},
  ) =>
    this.formatHrefURL(
      `/my/share-room/${roomId}${buildParams({
        verified: verified ? 1 : null,
      })}`,
    );

  getListingSuccessUrl = (roomId: number | string) =>
    this.formatHrefURL(`/my/listings/${roomId}/success`);

  getCalendarSyncGuideUrl() {
    return `${this.getBaseUrl()}/calendar-sync-guide`;
  }

  getFbAuthUrl = (searchParams: object) =>
    `https://www.facebook.com/dialog/oauth${buildParams(searchParams)}`;

  getGoogleAuthUrl = (searchParams: object) =>
    `https://accounts.google.com/o/oauth2/auth${buildParams(searchParams)}`;

  getFacebookShareUrl = (url: string, params: object) =>
    `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
      url,
    )}${buildParams(params)}`;

  getBecomeAnAdvertiserUrl() {
    return this.formatHrefURL('/become-an-advertiser');
  }

  getLoginUrl(redirectAfterUrl?: string) {
    if (!redirectAfterUrl) {
      return this.formatHrefURL('/login');
    }

    return this.formatHrefURL(
      `/login${buildParams({ return_url: redirectAfterUrl })}`,
    );
  }

  getRecoverAccountUrl() {
    return this.formatHrefURL('/recover-account');
  }

  getRegisterUrl(redirectAfterUrl?: string) {
    if (!redirectAfterUrl) {
      return this.formatHrefURL('/register');
    }

    return this.formatHrefURL(
      `/register${buildParams({ return_url: redirectAfterUrl })}`,
    );
  }

  getRedirectUrl = (url: string) => {
    const dashboardUrl = `${this.getBaseUrl()}${this.getDashboardUrl()}`;

    return !url || isAbsoluteUrl(url) || url.startsWith('//')
      ? dashboardUrl
      : url;
  };

  getCreateSearchAlertUrl = (alert: string, returnUrl: string) =>
    this.formatHrefURL(
      `/my/callback/create-search-alert${buildParams({ alert, returnUrl })}`,
    );

  getSearchUrl = (
    city: string,
    country: string,
    filters?: object,
    category?: string,
  ) => {
    let path = `/s/${encodeSearchQuery([city, country].join(', '))}`;

    if (category) {
      path = `${path}/${category}`;
    } else {
      path = `${path}${searchUrl(filters)}`;
    }

    return this.formatHrefURL(path);
  };

  getCityUrl = (countrySlug: string, citySlug: string) =>
    this.formatHrefURL(
      `/${capitalizeFirst(citySlug)}--${capitalizeFirst(countrySlug)}`,
    );

  getGuideUrl = (slug: string, countrySlug: string, citySlug?: string) => {
    const url = citySlug
      ? `/${capitalizeFirst(citySlug)}--${capitalizeFirst(countrySlug)}/${slug}`
      : `/${capitalizeFirst(countrySlug)}/${slug}`;

    return this.formatHrefURL(url);
  };

  getCountryUrl = (countrySlug: string, category?: string) => {
    const url = category
      ? `/${COUNTRIES_TRANSLATED[this.lang]}/${capitalizeFirst(
          countrySlug,
        )}/${category}`
      : `/${COUNTRIES_TRANSLATED[this.lang]}/${capitalizeFirst(countrySlug)}`;

    return this.formatHrefURL(url);
  };

  // create path to get hermes data for country page
  getHermesCountryPath = (countrySlug: string) =>
    this.formatHrefURL(`/${countrySlug}`);

  getCountryGuideUrl = (countrySlug: string) =>
    this.formatHrefURL(`/${capitalizeFirst(countrySlug)}`);

  getContactUrl = (lang?: Language) => this.formatHrefURL('/contact', lang);

  getUniversityUrl = (
    countrySlug: string,
    citySlug: string,
    universitySlug: string,
  ) =>
    this.formatHrefURL(
      `/${capitalizeFirst(citySlug)}--${capitalizeFirst(
        countrySlug,
      )}/${universitySlug.toLowerCase()}`,
    );

  getPartnershipsUrl = () => 'https://info.housinganywhere.com/partnerships';

  getMyListingsUrl = () => this.formatHrefURL('/my/listings');

  getMyInboxUrl = ({
    state,
    listingId,
  }: { state?: Kinds; listingId?: string | number } = {}) =>
    this.formatHrefURL(`/my/inbox${buildParams({ state, listingId })}`);

  getEditListingUrl = (listingId: number | string) =>
    this.formatHrefURL(`/my/listings/${listingId}/edit`);

  getEditDraftListingUrl = (listingId: number | string) =>
    this.formatHrefURL(`/my/listings/${listingId}/edit-draft`);

  getGetVerifiedUrl = (listingId: number | string) =>
    this.formatHrefURL(`/my/get-verified${buildParams({ roomId: listingId })}`);

  getVerifyPhoneNumberUrl = (listingId: number | string) =>
    this.formatHrefURL(`/my/verify-phone-number${buildParams({ listingId })}`);

  getTermsUrl = () => this.formatHrefURL('/terms');

  getLinkToUserPage = (userId: number | string) =>
    this.formatHrefURL(`/users/show/${userId}`);

  getLinkToEditUserPage = () => this.formatHrefURL(`/my/profile/edit`);

  getPressUrl = () => this.formatHrefURL('/press-releases');

  getLegalUrl = (lang?: Language) => this.formatHrefURL('/legal', lang);

  getCookiePolicyUrl = () => this.formatHrefURL('/cookie-policy');

  getDataSecurityUrl = () => this.formatHrefURL('/data-security');

  getSitemapUrl = (lang?: Language) => this.formatHrefURL('/sitemap', lang);

  getListingCalendarUrl = (id: number) =>
    this.formatHrefURL(`/my/listings/${id}/calendar`);

  getListYourPlaceUrl = () => this.formatHrefURL('/list-your-place');

  getPaymentRequestHandleUrl = (handle: string) =>
    this.absolutizePath(`/r/pay/${handle}`);

  getConversationUrl = (conversationId: number | string, from?: string) =>
    this.formatHrefURL(`/my/talk/${conversationId}${buildParams({ from })}`);

  getConversationBookingUrl = (conversationId: number | string) =>
    this.formatHrefURL(`/my/talk/${conversationId}/book-request`);

  getPaymentUrl = (conversationId: number | string) =>
    this.formatHrefURL(`/my/talk/${conversationId}/book`);

  getBillingUrl = () => this.formatHrefURL('/my/payouts/billing');

  getPaymentMethodsUrl = () => this.formatHrefURL('/my/payments/methods');

  getPaymentsPaymentRequestsUrl = () =>
    this.formatHrefURL('/my/payments/requests');

  getPayoutMethodsUrl = () => this.formatHrefURL('/my/payouts/preferences');

  getPayoutRoutingUrl = (payentMethodId: string) =>
    this.formatHrefURL(`/my/payouts/preferences/${payentMethodId}/routing`);

  getInvoicesUrl = () => this.formatHrefURL('/my/payouts/invoices');

  getTransactionHistoryUrl = () => this.formatHrefURL('/my/payouts/history');

  getPayoutsPaymentRequests = () =>
    this.formatHrefURL('/my/payouts/payment-requests');

  getPaymentPlan = (conversationId?: number | string) =>
    this.formatHrefURL(
      `/my/payment-plan${conversationId ? `/${conversationId}` : ''}`,
    );

  getCommissionPlanUrl = () => this.formatHrefURL('/my/payouts/plan');

  getPaymentResultUrl = () =>
    `${this.getBaseUrl()}${this.formatHrefURL('/adyenPaymentResult')}`;

  getPartnerRedirect = ({ roomid }: { roomid: number | string }) =>
    `/partner-redirect${buildParams({
      roomid,
    })}`;

  getPartnerDashboard = ({
    startDate,
    endDate,
  }: {
    startDate?: string;
    endDate?: string;
  }) => `/partner${buildParams({ start: startDate, end: endDate })}`;

  getFacebookUrl = () => socialUrls.facebookUrl;

  getTwitterUrl = () => socialUrls.twitterUrl;

  getLinkedinUrl = () => socialUrls.linkedinUrl;

  getYoutubeUrl = () => socialUrls.youtubeUrl;

  getBookingsUrl = () => this.formatHrefURL('/my/bookings');

  getUserProfileUrl = (userId: number | string) => `/my/profile/${userId}`;

  getAdminPagesUrl = () => '/admin';

  getFavoritesUrl = () => this.formatHrefURL('/my/favorites');

  getHomeUrl = () => this.formatHrefURL('/');

  getRenting = () =>
    HowItWorksUrls.renting[this.lang] || HowItWorksUrls.renting.en;

  getRentingOut = () =>
    HowItWorksUrls.rentingOut[this.lang] || HowItWorksUrls.rentingOut.en;

  getPricingTenantsUrl = () => this.formatHrefURL('/pricing/tenants');

  getPricingLandlordsUrl = () => this.formatHrefURL('/pricing/landlords');

  getRentRadarUrl = () => {
    return `https://rentradar.housinganywhere.com/${
      rentRadarLanguangesWithLeadFormSupported.includes(this.lang)
        ? 'lead/'
        : ''
    }${rentRadarLanguanges.includes(this.lang) ? this.lang : ''}`;
  };

  getKrugerPRUrl = () => krugerLangToPath[this.lang] || krugerLangToPath.en;

  getRenmateFAQUrl = () =>
    'https://answers.housinganywhere.com/en/articles/3732114-how-will-the-rentmate-acquisition-affect-me';

  getCoronaFAQUrl = () =>
    'https://answers.housinganywhere.com/en/articles/3746319-coronavirus-cancellation-policy';

  getContactAdvertiserUrl = (
    listingId: string,
    params: { startDate?: string | null; endDate?: string | null },
  ) =>
    this.formatHrefURL(`/my/book/contact/${listingId}${buildParams(params)}`);

  getSecurePaymentUrl = () => {
    switch (this.lang) {
      case Language.fr:
        return this.formatHrefURL('/paiements-securise', this.lang);
      case Language.de:
        return this.formatHrefURL('/sichere-bezahlung', this.lang);
      case Language.es:
        return this.formatHrefURL('/pagos-seguros', this.lang);
      case Language.it:
        return this.formatHrefURL('/pagamenti-sicuri', this.lang);
      case Language.nl:
        return this.formatHrefURL('/veilige-huurbetalingen', this.lang);
      default:
        return this.formatHrefURL('/secure-payments', Language.en);
    }
  };
}
